import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  Buildings, User, MapPin, Lock, Plus, Trash, Star, UserCircle,
  Pencil, IdentificationBadge, Envelope, Phone, FileText, House,
  Check, X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";

export default function ClientProfile() {
  const { t } = useI18n();
  const [tab, setTab] = useState("identity");
  const [profile, setProfile] = useState(null);

  const TABS = [
    { key: "identity", icon: IdentificationBadge, label: t("prof_tab_identity") },
    { key: "contact",  icon: User,               label: t("prof_tab_contact") },
    { key: "shipping", icon: MapPin,             label: t("prof_tab_shipping") },
    { key: "security", icon: Lock,               label: t("prof_tab_security") },
  ];

  const load = async () => {
    const { data } = await api.get("/client/profile");
    setProfile(data);
  };
  useEffect(() => { load(); }, []);

  if (!profile) return <div className="p-8 text-[var(--tf-muted)]">{t("loading")}</div>;

  const client = profile.client || {};
  const isPrivate = (client.entity_type || "business") === "private";
  const displayName = isPrivate
    ? `${client.first_name || ""} ${client.last_name || ""}`.trim() || client.company_name || t("profile")
    : client.company_name || t("profile");

  return (
    <div className="space-y-6" data-testid="client-profile">
      <PageHeader
        eyebrow={t("profile")}
        title={displayName}
        subtitle={isPrivate ? t("prof_private_subtitle") : t("prof_business_subtitle")}
        chip="tf-chip-lavender"
        icon={UserCircle}
      />

      {/* Tab pills - horizontal scroll disabled with flex-wrap */}
      <div className="flex flex-wrap gap-2" data-testid="profile-tabs">
        {TABS.map((tb) => {
          const active = tab === tb.key;
          return (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={`inline-flex items-center gap-2 rounded-full text-xs md:text-sm font-bold font-display transition-all ${
                active
                  ? "bg-[var(--tf-ink)] text-white shadow"
                  : "bg-white text-[var(--tf-ink)] border border-[var(--tf-border)] hover:border-[var(--tf-ink)]"
              }`}
              data-testid={`tab-${tb.key}`}
              style={{ padding: "10px 16px" }}
            >
              <tb.icon size={14} weight="bold" />
              {tb.label}
            </button>
          );
        })}
      </div>

      {tab === "identity" && <IdentityCard client={client} reload={load} />}
      {tab === "contact"  && <ContactTab client={client} reload={load} />}
      {tab === "shipping" && <ShippingTab addresses={profile.shipping_addresses} reload={load} />}
      {tab === "security" && <SecurityTab />}
    </div>
  );
}

/* ============ IDENTITY (Privato / Azienda) ============ */
function IdentityCard({ client, reload }) {
  const { t } = useI18n();
  const isPrivate = (client.entity_type || "business") === "private";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    entity_type: client.entity_type || "business",
    company_name: client.company_name || "",
    first_name: client.first_name || "",
    last_name: client.last_name || "",
    vat_number: client.vat_number || "",
    codice_fiscale: client.codice_fiscale || "",
    sdi_or_pec: client.sdi_or_pec || "",
    address_line1: client.address_line1 || client.billing_address || "",
    city: client.city || "",
    postal_code: client.postal_code || "",
    province: client.province || "",
    country: client.country || "IT",
    business_phone: client.business_phone || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/client/profile/company", form);
      toast.success(t("prof_data_updated"));
      setEditing(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setForm({
      entity_type: client.entity_type || "business",
      company_name: client.company_name || "",
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      vat_number: client.vat_number || "",
      codice_fiscale: client.codice_fiscale || "",
      sdi_or_pec: client.sdi_or_pec || "",
      address_line1: client.address_line1 || client.billing_address || "",
      city: client.city || "",
      postal_code: client.postal_code || "",
      province: client.province || "",
      country: client.country || "IT",
      business_phone: client.business_phone || "",
    });
    setEditing(false);
  };

  return (
    <div className="tf-card p-5 md:p-6 space-y-5" data-testid="identity-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isPrivate ? "bg-[#E7E1FA] text-[#4B33A0]" : "bg-[#DAECFA] text-[#1F5A93]"}`}>
            {isPrivate ? <User size={22} weight="fill" /> : <Buildings size={22} weight="fill" />}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">{t("prof_type_label")}</div>
            <div className="font-display font-black text-lg">{isPrivate ? t("prof_type_private") : t("prof_type_business")}</div>
          </div>
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="tf-btn tf-btn-ghost text-xs"
            data-testid="profile-edit-btn"
            style={{ padding: "10px 16px" }}
          >
            <Pencil size={14} weight="bold" /> {t("prof_edit")}
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={cancel} className="tf-btn tf-btn-ghost text-xs" data-testid="profile-cancel" style={{ padding: "10px 14px" }}>
              <XIcon size={14} weight="bold" /> {t("prof_cancel")}
            </button>
            <button onClick={save} disabled={saving} className="tf-btn tf-btn-primary text-xs" data-testid="profile-save" style={{ padding: "10px 14px" }}>
              <Check size={14} weight="bold" /> {saving ? "..." : t("prof_save")}
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setForm({ ...form, entity_type: "private" })}
            className={`tf-card p-4 text-left transition-all ${form.entity_type === "private" ? "border-2 border-[var(--tf-ink)] bg-[#F5F2E8]" : "border border-[var(--tf-border)] hover:border-[var(--tf-ink)]"}`}
            data-testid="entity-private"
          >
            <User size={20} weight="fill" className={form.entity_type === "private" ? "text-[var(--tf-ink)]" : "text-[var(--tf-muted)]"} />
            <div className="font-display font-bold text-sm mt-2">{t("prof_type_private")}</div>
            <div className="text-[11px] text-[var(--tf-muted)]">{t("prof_private_desc")}</div>
          </button>
          <button
            onClick={() => setForm({ ...form, entity_type: "business" })}
            className={`tf-card p-4 text-left transition-all ${form.entity_type === "business" ? "border-2 border-[var(--tf-ink)] bg-[#F5F2E8]" : "border border-[var(--tf-border)] hover:border-[var(--tf-ink)]"}`}
            data-testid="entity-business"
          >
            <Buildings size={20} weight="fill" className={form.entity_type === "business" ? "text-[var(--tf-ink)]" : "text-[var(--tf-muted)]"} />
            <div className="font-display font-bold text-sm mt-2">{t("prof_type_business")}</div>
            <div className="text-[11px] text-[var(--tf-muted)]">{t("prof_business_desc")}</div>
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {form.entity_type === "private" ? (
          <>
            <Field icon={User} label={t("prof_field_first_name")} value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} editing={editing} testid="field-first-name" />
            <Field icon={User} label={t("prof_field_last_name")} value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} editing={editing} testid="field-last-name" />
            <Field icon={FileText} label={t("prof_field_cf")} value={form.codice_fiscale} onChange={(v) => setForm({ ...form, codice_fiscale: v.toUpperCase() })} editing={editing} testid="field-cf" />
            <Field icon={Phone} label={t("prof_field_phone")} value={form.business_phone} onChange={(v) => setForm({ ...form, business_phone: v })} editing={editing} testid="field-phone" />
          </>
        ) : (
          <>
            <Field icon={Buildings} label={t("prof_field_company_name")} value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} editing={editing} testid="field-company-name" fullWidth />
            <Field icon={FileText} label={t("prof_field_vat")} value={form.vat_number} onChange={(v) => setForm({ ...form, vat_number: v })} editing={editing} testid="field-vat" />
            <Field icon={FileText} label={t("prof_field_cf")} value={form.codice_fiscale} onChange={(v) => setForm({ ...form, codice_fiscale: v.toUpperCase() })} editing={editing} testid="field-cf" />
            <Field icon={Envelope} label={t("prof_field_sdi")} value={form.sdi_or_pec} onChange={(v) => setForm({ ...form, sdi_or_pec: v })} editing={editing} testid="field-sdi" fullWidth />
            <Field icon={Phone} label={t("prof_field_phone")} value={form.business_phone} onChange={(v) => setForm({ ...form, business_phone: v })} editing={editing} testid="field-phone" />
          </>
        )}
      </div>

      <div className="pt-4 border-t border-[var(--tf-border)]">
        <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold mb-3 flex items-center gap-1.5">
          <House size={11} weight="fill" /> {t("prof_billing_address")}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field icon={MapPin} label={t("prof_field_address")} value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} editing={editing} testid="field-address" fullWidth />
          <Field icon={MapPin} label={t("prof_field_city")} value={form.city} onChange={(v) => setForm({ ...form, city: v })} editing={editing} testid="field-city" />
          <Field icon={MapPin} label={t("prof_field_cap")} value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v })} editing={editing} testid="field-cap" />
          <Field icon={MapPin} label={t("prof_field_province")} value={form.province} onChange={(v) => setForm({ ...form, province: v.toUpperCase() })} editing={editing} testid="field-province" />
          <Field icon={MapPin} label={t("prof_field_country")} value={form.country} onChange={(v) => setForm({ ...form, country: v.toUpperCase() })} editing={editing} testid="field-country" />
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, editing, testid, fullWidth, type = "text" }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={11} weight="bold" />}
        {label}
      </label>
      {editing ? (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-2xl border border-[var(--tf-border)] bg-white text-sm font-display font-bold text-[var(--tf-ink)] focus:outline-none focus:border-[var(--tf-ink)] focus:ring-2 focus:ring-[var(--tf-primary)]/30 transition"
          style={{ padding: "14px 16px" }}
          data-testid={testid}
        />
      ) : (
        <div
          className="w-full rounded-2xl bg-[#F5F2E8] text-sm font-display font-bold text-[var(--tf-ink)] min-h-[48px] flex items-center"
          style={{ padding: "14px 16px" }}
          data-testid={`${testid}-value`}
        >
          {value || <span className="text-[var(--tf-muted)] font-normal">—</span>}
        </div>
      )}
    </div>
  );
}

/* ============ CONTACT ============ */
function ContactTab({ client, reload }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    contact_name: client.contact_name || "",
    contact_email: client.contact_email || "",
    notification_emails: client.notification_emails || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/client/profile/contact", form);
      toast.success(t("prof_contacts_updated"));
      setEditing(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="tf-card p-5 md:p-6 space-y-4" data-testid="contact-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display font-bold text-xl">{t("prof_contact_title")}</h3>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="tf-btn tf-btn-ghost text-xs" data-testid="contact-edit" style={{ padding: "10px 16px" }}>
            <Pencil size={14} weight="bold" /> {t("prof_edit")}
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="tf-btn tf-btn-ghost text-xs" style={{ padding: "10px 14px" }}>
              <XIcon size={14} weight="bold" /> {t("prof_cancel")}
            </button>
            <button onClick={save} disabled={saving} className="tf-btn tf-btn-primary text-xs" data-testid="contact-save" style={{ padding: "10px 14px" }}>
              <Check size={14} weight="bold" /> {saving ? "..." : t("prof_save")}
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field icon={User} label={t("prof_contact_name")} value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} editing={editing} testid="contact-name" />
        <Field icon={Envelope} label={t("prof_contact_email")} value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} editing={editing} testid="contact-email" />
      </div>
      <Field icon={Envelope} label={t("prof_notif_emails")} value={form.notification_emails} onChange={(v) => setForm({ ...form, notification_emails: v })} editing={editing} testid="contact-notif" fullWidth />
    </div>
  );
}

/* ============ SHIPPING ============ */
function ShippingTab({ addresses, reload }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ label: "", address_line: "", city: "", zip: "", country: "IT", is_default: false });
  const [adding, setAdding] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/profile/shipping-addresses", form);
      toast.success(t("prof_address_added"));
      setForm({ label: "", address_line: "", city: "", zip: "", country: "IT", is_default: false });
      setAdding(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };
  const remove = async (id) => {
    if (!window.confirm(t("prof_remove_confirm"))) return;
    await api.delete(`/client/profile/shipping-addresses/${id}`);
    reload();
  };
  return (
    <div className="space-y-4" data-testid="shipping-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-xl">{t("prof_shipping_title")}</h3>
          <p className="text-xs text-[var(--tf-muted)] mt-0.5">{t("prof_shipping_sub")}</p>
        </div>
        <button onClick={() => setAdding(!adding)} className="tf-btn tf-btn-primary" data-testid="add-address" style={{ padding: "12px 18px" }}>
          <Plus size={14} weight="bold" /> {t("prof_add_address")}
        </button>
      </div>
      {adding && (
        <form onSubmit={submit} className="tf-card p-5 space-y-4" data-testid="address-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field icon={FileText} label={t("prof_addr_label")} value={form.label} onChange={(v) => setForm({ ...form, label: v })} editing testid="addr-label" />
            <Field icon={MapPin} label={t("prof_addr_country")} value={form.country} onChange={(v) => setForm({ ...form, country: v.toUpperCase() })} editing testid="addr-country" />
          </div>
          <Field icon={House} label={t("prof_addr_address")} value={form.address_line} onChange={(v) => setForm({ ...form, address_line: v })} editing testid="addr-line" fullWidth />
          <div className="grid md:grid-cols-2 gap-4">
            <Field icon={MapPin} label={t("prof_addr_city")} value={form.city} onChange={(v) => setForm({ ...form, city: v })} editing testid="addr-city" />
            <Field icon={MapPin} label={t("prof_addr_zip")} value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} editing testid="addr-zip" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="addr-default" />
            {t("prof_addr_default")}
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setAdding(false)} className="tf-btn tf-btn-ghost flex-1" style={{ padding: "14px 18px" }}>{t("prof_cancel")}</button>
            <button type="submit" className="tf-btn tf-btn-primary flex-1" data-testid="addr-submit" style={{ padding: "14px 18px" }}>{t("prof_save_address")}</button>
          </div>
        </form>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {addresses.length === 0 && !adding && (
          <div className="tf-card p-8 md:col-span-2 text-center">
            <MapPin size={32} weight="duotone" className="mx-auto text-[var(--tf-muted)]" />
            <p className="text-[var(--tf-muted)] mt-2 text-sm">{t("prof_no_addresses")}</p>
          </div>
        )}
        {addresses.map((a) => (
          <div key={a.id} className="tf-card p-5" data-testid={`address-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-sm truncate">{a.label}</div>
                  {a.is_default && (
                    <span className="tf-status-pill tf-status-info text-[10px]">
                      <Star size={10} weight="fill" /> {t("prof_default_badge")}
                    </span>
                  )}
                </div>
                <div className="text-sm mt-2 text-[var(--tf-ink)]">{a.address_line}</div>
                <div className="text-xs text-[var(--tf-muted)] mt-0.5">{a.zip} {a.city} · {a.country}</div>
              </div>
              <button onClick={() => remove(a.id)} className="w-9 h-9 rounded-xl flex items-center justify-center text-[var(--tf-muted)] hover:text-[var(--tf-error)] hover:bg-[#FBDDE1] transition flex-shrink-0" data-testid={`del-addr-${a.id}`} title={t("common_remove")}>
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ SECURITY ============ */
function SecurityTab() {
  const { t } = useI18n();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error(t("prof_password_mismatch")); return; }
    if (form.new_password.length < 6) { toast.error(t("prof_password_min")); return; }
    setSaving(true);
    try {
      await api.post("/client/profile/password", { current_password: form.current_password, new_password: form.new_password });
      toast.success(t("prof_password_updated"));
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="tf-card p-5 md:p-6 space-y-4 max-w-lg" data-testid="security-card">
      <h3 className="font-display font-bold text-xl">{t("prof_change_password")}</h3>
      <Field icon={Lock} label={t("prof_current_password")} type="password" value={form.current_password} onChange={(v) => setForm({ ...form, current_password: v })} editing testid="pw-current" />
      <Field icon={Lock} label={t("prof_new_password")} type="password" value={form.new_password} onChange={(v) => setForm({ ...form, new_password: v })} editing testid="pw-new" />
      <Field icon={Lock} label={t("prof_confirm_password")} type="password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} editing testid="pw-confirm" />
      <button type="submit" disabled={saving} className="tf-btn tf-btn-primary" data-testid="pw-submit" style={{ padding: "14px 20px" }}>
        <Check size={14} weight="bold" /> {saving ? "..." : t("prof_update_password")}
      </button>
    </form>
  );
}
